import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A marca em que a pessoa está trabalhando — <b>um recorte de tela, nunca de segurança</b>.
 *
 * <p>A distinção é a razão de este contexto existir separado da autenticação. Quem limita o
 * que a pessoa pode ver é o token: quando o vínculo tem `brandId`, o servidor devolve só
 * aquela marca, e nada que o painel faça amplia isso. O seletor da barra superior é
 * conveniência para quem tem acesso a todas — filtrar a tela sem trocar de sessão.
 *
 * <p>Por isso ele mora na URL das telas que filtram, e não numa variável escondida: um
 * recorte que não aparece no endereço produz o pior bilhete de suporte possível — "o item
 * não aparece para mim e aparece para você", com os dois olhando a mesma tela.
 */

interface Estado {
  marcaAtiva: string | null;
  escolherMarca: (id: string | null) => void;
}

const Contexto = createContext<Estado | null>(null);

const CHAVE = 'confira.marca';

export function MarcaAtivaProvider({ children }: { children: ReactNode }) {
  const [marcaAtiva, setMarca] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(CHAVE);
    } catch {
      return null;
    }
  });

  const escolherMarca = useCallback((id: string | null) => {
    setMarca(id);
    try {
      if (id) {
        sessionStorage.setItem(CHAVE, id);
      } else {
        sessionStorage.removeItem(CHAVE);
      }
    } catch {
      // Armazenamento bloqueado: a escolha vale para esta navegação e pronto.
    }
  }, []);

  const valor = useMemo(() => ({ marcaAtiva, escolherMarca }), [marcaAtiva, escolherMarca]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useMarcaAtiva(): Estado {
  const c = useContext(Contexto);
  if (!c) {
    throw new Error('useMarcaAtiva fora do MarcaAtivaProvider');
  }
  return c;
}
